<template>
  <v-card v-if="project.lifecycle">
    <v-card-title>
      <div class="title">
        {{ projectType.name }}
      </div>
    </v-card-title>

    <v-stepper
      v-if="latestMilestone"
      v-model="latestMilestone"
      class="elevation-0 project-milestones"
      non-linear
    >
      <v-stepper-header>
        <template v-for="(milestone, i) in projectType.milestones">
          <v-divider
            v-if="i !== 0"
            :key="`m-divider-${i}`"
          />
          <v-stepper-step
            :key="`m-step-${i}`"
            :step="i + 1"
            edit-icon="check"
            :complete="isPassedMilestone(i)"
            editable
            :rules="[() => isPassedMilestone(i, true)]"
          >
            {{ milestone.name }}
            <small>{{ milestone.shortDescription }}</small>
          </v-stepper-step>
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="(milestone, mId) in projectType.milestones"
          :key="`content-${mId}`"
          :step="mId + 1"
        >
          <v-container>
            <v-layout
              row
              wrap
            >
              <v-flex
                sm6
                xs12
              >
                <v-select
                  v-model="milestones[mId].outcome"
                  label="Outcome"
                  :items="allMilestoneOutcomes"
                  clearable
                  :disabled="!isEditableMilestone(mId) || loading"
                  @input="save(mId)"
                />
              </v-flex>

              <v-flex
                sm6
                xs12
              >
                <pa-date-picker
                  v-model="milestones[mId].plannedDate"
                  label="Planned Date"
                  :disabled="loading"
                  @input="save(mId)"
                />
                <pa-date-picker
                  v-model="milestones[mId].actualDate"
                  label="Actual Date"
                  :disabled="!isEditableMilestone(mId) || loading"
                  @input="save(mId)"
                />
              </v-flex>

              <v-flex
                sm6
                xs12
              >
                <v-card
                  v-if="milestone.documents"
                  class="elevation-1"
                >
                  <pa-card-toolbar
                    title="Documentation"
                    :color="null"
                    dense
                  />
                  <v-list dense>
                    <template v-for="(doc, docId) in milestone.documents">
                      <v-divider
                        v-if="docId != 0"
                        :key="'docdiv-'+docId"
                      />
                      <v-list-item :key="'doc-'+docId">
                        <v-list-item-action>
                          <v-btn
                            download
                            :href="getMilestoneDoc(mId, docId)"
                            icon
                          >
                            <v-icon
                              :disabled="!getMilestoneDoc(mId, docId)"
                              :color="getMilestoneDoc(mId, docId) ? 'success' : null"
                            >
                              insert_drive_file
                            </v-icon>
                          </v-btn>
                        </v-list-item-action>
                        <v-list-item-content>
                          {{ getOrganizationDoc(doc).name }}
                        </v-list-item-content>
                        <v-list-item-action>
                          <vu-file-upload
                            accept="*/*"
                            :firebase-storage="getDocFilename(getOrganizationDoc(doc), milestone)"
                            :firestore-ref="getDocRef(mId)"
                            :firestore-field="`documentation.${docId}`"
                            type="button"
                          />
                        </v-list-item-action>
                        <v-list-item-action>
                          <v-btn
                            icon
                            :disabled="!getMilestoneDoc(mId, docId)"
                            @click="deleteDoc(mId, docId)"
                          >
                            <v-icon>close</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                  </v-list>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-card>

  <v-flex
    v-else
    xs12
    md6
    offset-md3
  >
    <v-card
      flat
      color="transparent"
    >
      <v-card-title class="title justify-center">
        <div>Getting started with Project Life Cycle and Phases</div>
      </v-card-title>

      <v-card-text class="text-justify">
        <v-alert
          type="info"
          outlined
          class="mb-6"
        >
          If you just need to create milestones, you can create an activity with zero duration.
        </v-alert>

        <p>
          Project life cycle is a series of phases that represent the evolution of a product,
          from concept through delivery, maturity, and to retirement.
          The reason why a project may be broken up into phases is because it provides
          more control over the project and greater clarity with respect to the project deliverables.
          Each phase should be treated as a mini-project (i.e. it has all the 5 process groups from
          initiating to closure).
        </p>

        <p>
          You should use phases if the following criteria apply:
          <ul>
            <li>it is a large project (in duration, budget, personnel, ...)</li>
            <li>
              it is a recurring project, you have many of this kind
              (installation/setup at a customer, new product development, ...)
            </li>
            <li>
              you have a well-defined procedure to manage the project life cycle
              (how you proceed to next phase, who approves it, ...)
            </li>
          </ul>
        </p>

        <p>
          Using life cycles gives you the advantage to define your project management
          process and procedures inside Atlas and run analysis on the phases.
        </p>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn
          color="primary"
          :to="{ name: 'project-edit', params: { name: projectId } }"
        >
          Set Project Type
        </v-btn>

        <open-help-btn />
      </v-card-actions>
    </v-card>
  </v-flex>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { firestore } from '@/database'
import storage from '@/storage'
import ProjectComponent from '@/mixins/ProjectComponent'
import PaCardToolbar from '@/components/shared/PaCardToolbar'
import PaDatePicker from '@/components/shared/PaDatePicker'
import VuFileUpload from '@/components/shared/VuFileUpload'

export default {
  components: {
    PaCardToolbar,
    PaDatePicker,
    VuFileUpload
  },
  mixins: [ ProjectComponent ],
  props: {
    project: {
      type: Object,
      default: () => { return {} }
    }
  },
  data () {
    return {
      latestMilestone: null,
      milestones: {},
      milestonesLUT: {},
      allMilestoneOutcomes: [
        { text: 'Passed', value: true },
        { text: 'Not Passed', value: false }
      ],
      projectType: {},
      loading: false
    }
  },
  computed: {
    ...mapGetters('organizations', [
      'getProjectTypeById',
      'getOrganizationDoc'
    ])
  },
  methods: {
    ...mapMutations([ 'setAlert' ]),
    isPassedMilestone (index, defaultOutcome = false) {
      const milestone = this.milestones[index]

      if (typeof milestone.outcome === 'boolean') {
        return milestone.outcome
      }

      return defaultOutcome
    },
    isEditableMilestone (index) {
      return index === 0 ||
          this.isPassedMilestone(index) ||
          this.isPassedMilestone(index - 1)
    },
    getDocFilename (doc, milestone) {
      return `/attachments/${this.projectId}/${this.project.name}-${doc.name}-${milestone.name}`
    },
    getDocRef (milestoneId) {
      const mId = this.milestonesLUT[milestoneId]
      if (!mId) { return null }
      return firestore.collection('milestones').doc(mId)
    },
    getMilestoneDoc (milestoneId, docId) {
      const mDocs = this.milestones[milestoneId].documentation
      return mDocs ? mDocs[docId] : null
    },
    deleteDoc (milestoneId, docId) {
      storage.ref('/').child(this.milestones[milestoneId].docs[docId].fullPath).delete()
        .then(() => {
          this.milestones[milestoneId].docs.splice(docId, 1)
          this.save()
        })
    },
    save (id) {
      this.loading = true

      // id is numeric sort index of the milestone, mId is firestore id
      if (!isFinite(id)) { throw new Error('missing id in save()') }
      const mId = this.milestonesLUT[id]
      const data = this.milestones[id]

      let docRef = firestore.collection('milestones')

      if (mId) {
        docRef = docRef.doc(mId)
      } else {
        docRef = docRef.doc()
      }

      docRef.set(data)
        .then(() => {
          this.loading = false
          this.setAlert({ message: 'Milestone saved' })
        })
        .catch(err => {
          this.loading = false
          this.setAlert({ message: err.message, error: true })
          throw err
        })

      // Update the latest milestone if the user set a milestone outcome
      // on a milestone successive to the latest one
      if (data.outcome !== null && (!this.project.latestMilestone ||
          this.project.latestMilestone.milestoneId <= id)) {
        firestore.collection('projects').doc(this.projectId)
          .update({ latestMilestone: {
            milestoneId: id,
            outcome: this.isPassedMilestone(id) }
          })
      }
    },
    loadData () {
      this.latestMilestone = 1
      this.projectType = this.getProjectTypeById(this.project.lifecycle)
      if (!this.projectType) { return }

      this.milestones = {}
      this.milestonesLUT = {}

      for (let i in this.projectType.milestones) {
        this.$set(this.milestones, i, {
          milestoneId: i,
          outcome: null,
          project: this.projectId,
          organization: this.$currentOrganization.id
        })
      }

      let latestMilestoneDone = false

      const routeMilestone = this.$route.query.m + 1

      firestore.collection('milestones')
        .where('project', '==', this.projectId)
        .orderBy('milestoneId')
        .onSnapshot(querySnapshot => {
          querySnapshot.forEach(doc => {
            let milestone = doc.data()
            this.milestonesLUT[milestone.milestoneId] = doc.id
            milestone.actualDate = milestone.actualDate ? milestone.actualDate.toDate() : null
            milestone.plannedDate = milestone.plannedDate ? milestone.plannedDate.toDate() : null
            this.milestones[milestone.milestoneId] = milestone

            if (!routeMilestone && !latestMilestoneDone && this.isPassedMilestone(milestone.milestoneId)) {
              this.latestMilestone = parseInt(milestone.milestoneId) + 1
            }
          })
          latestMilestoneDone = true

          if (routeMilestone) { this.latestMilestone = routeMilestone }
          if (!this.latestMilestone) { this.latestMilestone = 1 }
        }, err => {
          this.setAlert({ message: 'Can\'t load milestones', error: true })
          throw err
        })
    }
  }
}
</script>

<style lang="scss">
.project-milestones {
  .v-stepper__header {
    flex-wrap: nowrap;
    overflow-x: auto;

    .v-stepper__step {
      flex: 0 0 auto;
    }
  }
}
</style>
